<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && claim"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-md w-full">
            <!-- Header -->
            <div class="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-6 py-4 flex items-center">
              <div class="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-red-900 dark:text-red-100">
                Reject Claim
              </h3>
            </div>

            <!-- Content -->
            <div class="px-6 py-6">
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to reject this claim?
              </p>

              <!-- Claim Details -->
              <div class="bg-gray-50 dark:bg-black rounded-lg p-4 space-y-2 mb-4">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Claim Number:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">{{ claim.claim_number }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Customer:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ claim.user?.first_name }} {{ claim.user?.last_name }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Tire:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ claim.tire?.pattern }} - {{ claim.tire?.tire_size }}
                  </span>
                </div>
              </div>

              <!-- Rejection Reason -->
              <div class="mb-4">
                <label for="rejection-reason" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rejection Reason (Optional)
                </label>
                <textarea
                  id="rejection-reason"
                  v-model="rejectionReason"
                  rows="4"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-black text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:outline-none shadow-sm transition-colors duration-200 brand-focus-ring"
                  placeholder="Enter the reason for rejecting this claim..."
                ></textarea>
              </div>

              <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p class="text-sm text-red-800 dark:text-red-300">
                  <strong>Warning:</strong> This action will mark the claim as rejected and notify the customer.
                </p>
              </div>
            </div>

            <!-- Footer -->
            <div class="bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                @click="handleClose"
                :disabled="loading"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="handleConfirm"
                :disabled="loading"
                class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="loading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{{ loading ? 'Rejecting...' : 'Reject Claim' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { Claim } from '@/types/claim';

const props = defineProps<{
  isOpen: boolean;
  claim: Claim | null;
  loading?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  confirm: [rejectionReason: string];
}>();

const rejectionReason = ref('');

// Reset rejection reason when modal closes
watch(() => props.isOpen, (newValue) => {
  if (!newValue) {
    rejectionReason.value = '';
  }
});

function handleClose() {
  rejectionReason.value = '';
  emit('close');
}

function handleConfirm() {
  emit('confirm', rejectionReason.value);
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.brand-focus-ring:focus {
  --tw-ring-color: var(--brand-primary);
  border-color: var(--brand-primary);
}
</style>

